import App from "../app";
import { AnswerEvent, FinishEvent } from "../events";
import { ListItem } from "../model/datasource/list";
import { ContentItem, ItemType } from "../model/datasource/item";
import Controller from "./controller";
import { AttemptId, ProductId, ItemId, QuizId } from "../model/datasource";
import { Annotations, AnswerType } from "../lib/userdata";
import Model from "../model/model";

export class TestController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends Controller<P, A, Z, I> {
	bind(target: EventTarget, app: App<P, A, Z, I>): void {
		target.addEventListener(AnswerEvent.type, (e: AnswerEvent) => this.answer(app, e.detail));
		target.addEventListener(FinishEvent.type, () => this.finish(app));
	}

	answer(app: App<P, A, Z, I>, answer: AnswerType): void {
		const current: number = app.current.get();
		let item: I | undefined;
		Promise.all([
			app.model.metadata.getProductId(),
			app.model.metadata.getAttempt(),
			app.content.get(current).catch(() => undefined),
			app.content.meta(current),
		]).then((
			[product, attempt, content, meta]:
			[product: P, attempt: A, content: ContentItem<I> | undefined, meta: ListItem<I>]
		) => {
			item = content?.id ?? meta.id;
			app.view.list.displaySelectedAnswer(item, answer, "loading");
			const userdata = app.userdata.set(current, item, { answer });
			return Model.retryPromise(() =>
				app.model.item.setUserData(product, attempt, item, userdata)
			).then(() => ({item, userdata, type: content?.type}));
		}).then(({item, userdata, type}: {item: I, userdata: Annotations, type: ItemType | undefined}) => {
			app.model.list.setAttributes(item, userdata);
			if (type) {
				if (current === app.current.get()) {
					app.view.item.displaySelectedAnswer(type, answer);
				}
				app.view.header.displayProgress(
					app.view.chart.getTestProgressChart(),
					app.userdata.count((u: Annotations) => !!u?.answer),
					app.content.count()
				);
				app.view.list.displaySelectedAnswer(item, answer);
			}
		}).catch(err => {
			if (item) { app.view.list.displaySelectedAnswer(item, answer, "error"); }
		});
	}

	finish(app: App<P, A, Z, I>): void {
		app.view.item.displayFinishing('loading');
		Promise.all([
			app.model.metadata.getProductId(),
			app.model.metadata.getAttempt()
		]).then((record: [P, A]) => ({
			product: record[0],
			attempt: record[1]
		})).then((record: { product: P, attempt: A }) =>
			app.model.item.setFinished(record.product, record.attempt)
		).then((url: string) => {
			app.view.item.displayResults(url);
		}).catch(() => {
			app.view.item.displayFinishing('error');
		});
	}
}